import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, FormControlLabel,
  FormGroup, Tabs, Tab, Snackbar,
} from '@mui/material';
import {
  Calculate, Settings, ExpandMore, Person, Group, TrendingUp, AccessTime,
  EventBusy, Save, EventRepeat, Warning,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  useLazyPreviewNonBankAnnualLeaveQuery,
  useApplyNonBankAnnualLeaveMutation,
  useEndOfYearNonBankAnnualLeaveMutation,
  useGetNonBankLeaveConfigQuery,
  useUpdateNonBankLeaveConfigMutation,
} from '../../../slices/nonBankAnnualLeaveApiSlice';
import { useGetStaffListQuery } from '../../../slices/usersApiSlice';

// Full list of staff categories the app knows about (mirrors LeaveConfig's
// default categoryHours list) — used to build the staff-category checkbox
// list in the Configuration tab. jobtitle is free text on the user record,
// so this is a starting point, not an enum; admins can add a custom one too.
const KNOWN_CATEGORIES = [
  'Bank-HCA', 'Bank-RGN', 'ECT-HCA', 'Nurse', 'Doctor', 'HCA', 'AHP', 'Admin', 'Other', 'Testing',
];

const fmtHrs = (h) => {
  if (h === null || h === undefined || h === '' || Number.isNaN(Number(h))) return '—';
  const totalMinutes = Math.round(Number(h) * 60);
  const sign = totalMinutes < 0 ? '-' : '';
  const abs = Math.abs(totalMinutes);
  const hrs = Math.floor(abs / 60);
  const mins = abs % 60;
  return `${sign}${hrs}:${String(mins).padStart(2, '0')}`;
};

const firstOfYear = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
};
const todayStr = () => new Date().toISOString().slice(0, 10);

const NonBankAnnualLeaveCalcScreen = () => {
  const [tab, setTab] = useState(0);
  const [startDate, setStartDate] = useState(firstOfYear());
  const [endDate, setEndDate]     = useState(todayStr());
  const [targetUser, setTargetUser] = useState('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [endOfYearConfirmOpen, setEndOfYearConfirmOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [detailRow, setDetailRow] = useState(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [localCategories, setLocalCategories] = useState(null);
  const [customCategory, setCustomCategory] = useState('');
  const [localStartDateConfig, setLocalStartDateConfig] = useState('');
  // Keeps the Calculate tab's Start Date synced to the configured
  // Calculation Period Start Date whenever config data arrives or changes
  // (including right after it's saved on the Configuration tab, in the same
  // session, with no page reload needed) — but only until the admin edits
  // the Start Date field themselves, at which point their edit wins and this
  // stops overwriting it.
  const [startDateUserEdited, setStartDateUserEdited] = useState(false);

  const { data: users = [] } = useGetStaffListQuery();

  const { data: serverConfig, isLoading: configLoading } = useGetNonBankLeaveConfigQuery();
  const [triggerPreview, { data: previewData, isFetching: previewing, error: previewError }] =
    useLazyPreviewNonBankAnnualLeaveQuery();
  const [applyAL,      { isLoading: applying }] = useApplyNonBankAnnualLeaveMutation();
  const [endOfYear,    { isLoading: rollingOver }] = useEndOfYearNonBankAnnualLeaveMutation();
  const [updateConfig, { isLoading: saving }]   = useUpdateNonBankLeaveConfigMutation();

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  useEffect(() => {
    if (serverConfig && !localCategories) {
      setLocalCategories([...(serverConfig.staffCategories || [])]);
    }
    if (serverConfig) {
      setLocalStartDateConfig(
        serverConfig.calculationPeriodStartDate
          ? String(serverConfig.calculationPeriodStartDate).slice(0, 10)
          : ''
      );
    }
    if (serverConfig && serverConfig.calculationPeriodStartDate && !startDateUserEdited) {
      setStartDate(String(serverConfig.calculationPeriodStartDate).slice(0, 10));
    }
  }, [serverConfig, startDateUserEdited]);

  const handlePreview = () => {
    triggerPreview({
      startDate,
      endDate,
      ...(targetUser !== 'all' && { userId: targetUser }),
    });
    setTab(1);
  };

  const handleApply = async () => {
    setConfirmOpen(false);
    try {
      const userIds = targetUser !== 'all'
        ? [targetUser]
        : previewData?.results?.map((r) => r.userId) || [];
      const res = await applyAL({ startDate, endDate, userIds }).unwrap();
      showSnack(`Applied to ${res.applied?.length} staff member(s)`, 'success');
      if (res.errors?.length > 0) {
        console.error('Apply errors:', res.errors);
        showSnack(`${res.errors.length} error(s) — check console`, 'warning');
      }
    } catch (err) {
      showSnack(err?.data?.message || 'Failed to apply', 'error');
    }
  };

  const handleEndOfYear = async () => {
    setEndOfYearConfirmOpen(false);
    try {
      const userIds = targetUser !== 'all' ? [targetUser] : [];
      const res = await endOfYear({ userIds }).unwrap();
      showSnack(`End of Year complete for ${res.applied?.length} staff member(s)`, 'success');
      if (res.errors?.length > 0) {
        console.error('End of Year errors:', res.errors);
        showSnack(`${res.errors.length} error(s) — check console`, 'warning');
      }
    } catch (err) {
      showSnack(err?.data?.message || 'Failed to run End of Year process', 'error');
    }
  };

  const toggleCategory = (category) => {
    setLocalCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const addCustomCategory = () => {
    const trimmed = customCategory.trim();
    if (!trimmed) return;
    setLocalCategories((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setCustomCategory('');
  };

  const handleSaveConfig = async () => {
    try {
      await updateConfig({
        staffCategories: localCategories,
        calculationPeriodStartDate: localStartDateConfig || null,
      }).unwrap();
      showSnack('Configuration saved', 'success');
      setEditingConfig(false);
    } catch (err) {
      showSnack(err?.data?.message || 'Failed to save config', 'error');
    }
  };

  // Only offer staff whose category is actually included in this calculator's
  // "All Staff (Batch)" run (configured on this screen's Configuration tab) —
  // picking someone outside that list here would be misleading, since Apply
  // would never touch them in a batch run.
  const eligibleUsers = users.filter((u) =>
    (serverConfig?.staffCategories || []).includes(u.jobtitle)
  );

  const results = previewData?.results || [];

  const columns = [
    {
      field: 'name', headerName: 'Staff Member', width: 175,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person fontSize="small" color="action" />
          <Typography variant="body2">{p.value}</Typography>
        </Box>
      ),
    },
    { field: 'jobtitle', headerName: 'Category', width: 130 },
    {
      field: 'bringForward', headerName: 'Bring Forward', width: 130, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="text.secondary">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'alTakenHours', headerName: 'AL Taken', width: 110, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="error.main">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'existingOpeningBalance', headerName: 'Current Opening Bal.', width: 150, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="text.secondary">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'newOpeningBalance', headerName: 'New Opening Bal.', width: 145, type: 'number',
      renderCell: (p) => (
        <Chip
          label={fmtHrs(p.value)}
          size="small"
          color={p.value >= 0 ? 'success' : 'error'}
          variant="filled"
        />
      ),
    },
    {
      field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => setDetailRow(p.row)}>Detail</Button>
      ),
    },
  ];

  const rows = results.map((r) => ({ ...r, id: r.userId }));

  const totalALTaken = results.reduce((s, r) => s + (r.alTakenHours || 0), 0);
  const totalTrue    = results.reduce((s, r) => s + (r.trueBalance  || 0), 0);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Calculate sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4">Non Bank Annual Leave</Typography>
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            Opening-balance deduction calculator (all non-Bank staff categories) · Admin Panel
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        This calculator does not run the statutory 3-method calculation used for Bank-HCA / Bank-RGN staff.
        These staff categories already have annual leave and public holiday time built into their
        <strong> Bring-Forward</strong> balance for the year, so this tool simply
        <strong> subtracts Annual Leave taken in the selected period from Bring-Forward</strong> and writes the
        result into Opening Balance. Just like the main calculator, it always recomputes fully from
        Bring-Forward — it never builds on top of whatever Opening Balance currently holds — so it's safe to
        run over and over as the period changes; you'll always get the correct result. If a staff member's
        starting allowance needs adjusting, an admin can add to or subtract from their Bring-Forward directly.
        Each Annual Leave day is also netted down by the same rest-break deduction tiers configured per staff
        category on the main calculator's Configuration tab (e.g. up to 4 hrs = no deduction, up to 6 hrs =
        0:30 deducted, over that = 1 hr deducted, or a category's own custom rule) before it's taken off the
        balance.
      </Alert>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Calculate" icon={<Calculate />} iconPosition="start" />
        <Tab label={`Results ${results.length > 0 ? `(${results.length})` : ''}`} icon={<TrendingUp />} iconPosition="start" />
        <Tab label="Configuration" icon={<Settings />} iconPosition="start" />
      </Tabs>

      {/* ══════════════ TAB 0: CALCULATE ══════════════ */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime color="primary" /> Calculation Period
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Start Date" type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setStartDateUserEdited(true); }}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="End Date" type="date"
                    value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Staff Member</InputLabel>
                    <Select value={targetUser} label="Staff Member" onChange={(e) => setTargetUser(e.target.value)}>
                      <MenuItem value="all">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Group fontSize="small" /> All Staff (Batch — {eligibleUsers.length} staff)
                        </Box>
                      </MenuItem>
                      {[...eligibleUsers]
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((u) => (
                          <MenuItem key={u._id} value={u._id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Person fontSize="small" />
                              {u.name}
                              {u.jobtitle && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {u.jobtitle}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" startIcon={<Calculate />}
                  onClick={handlePreview} disabled={previewing} fullWidth>
                  {previewing ? <CircularProgress size={22} /> : 'Preview Calculation'}
                </Button>
              </Box>
              {previewError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {previewError?.data?.message || 'Failed to preview calculation'}
                </Alert>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #f5c6a5' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventRepeat color="warning" /> End of Year Rollover
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                For the staff member selected above (or every eligible staff member, if left on "All Staff"),
                copies their current <strong>Opening Balance</strong> into <strong>Bring Forward</strong> and
                resets Opening Balance to <strong>0</strong>. Run this once, after the final Apply of the
                outgoing leave year, so next year's calculations start from a clean Opening Balance without
                losing whatever was left over.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This overwrites Bring Forward. If Bring Forward for the new year also needs the next
                allowance added on top, do that separately afterwards.
              </Alert>
              <Button variant="outlined" color="warning" startIcon={<EventRepeat />}
                onClick={() => setEndOfYearConfirmOpen(true)} disabled={rollingOver} fullWidth>
                {rollingOver ? <CircularProgress size={22} /> : 'Run End of Year Process'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ══════════════ TAB 1: RESULTS ══════════════ */}
      {tab === 1 && (
        <Box>
          {results.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Staff</Typography>
                  <Typography variant="h6">{results.length}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Total AL Taken</Typography>
                  <Typography variant="h6" color="error.main">{fmtHrs(totalALTaken)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Total New Opening Bal.</Typography>
                  <Typography variant="h6" color={totalTrue >= 0 ? 'success.main' : 'error.main'}>{fmtHrs(totalTrue)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button variant="contained" color="primary" fullWidth sx={{ height: '100%' }}
                  onClick={() => setConfirmOpen(true)} disabled={applying || results.length === 0}>
                  {applying ? <CircularProgress size={22} /> : 'Apply to Records'}
                </Button>
              </Grid>
            </Grid>
          )}

          <Paper sx={{ height: 560 }}>
            <DataGrid rows={rows} columns={columns} density="compact"
              disableRowSelectionOnClick loading={previewing}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[25, 50, 100]} />
          </Paper>
        </Box>
      )}

      {/* ══════════════ TAB 2: CONFIGURATION ══════════════ */}
      {tab === 2 && (
        <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0', maxWidth: 640 }}>
          <Typography variant="h6" gutterBottom>Staff Categories Included</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only staff whose category (job title) is checked below are included when "All Staff (Batch)"
            is run. This should be every category NOT covered by the main Bank-HCA / Bank-RGN calculator.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {configLoading || !localCategories ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <FormGroup>
                {KNOWN_CATEGORIES.map((cat) => (
                  <FormControlLabel
                    key={cat}
                    control={
                      <Checkbox
                        checked={localCategories.includes(cat)}
                        onChange={() => { toggleCategory(cat); setEditingConfig(true); }}
                      />
                    }
                    label={cat}
                  />
                ))}
                {localCategories.filter((c) => !KNOWN_CATEGORIES.includes(c)).map((cat) => (
                  <FormControlLabel
                    key={cat}
                    control={
                      <Checkbox
                        checked
                        onChange={() => { toggleCategory(cat); setEditingConfig(true); }}
                      />
                    }
                    label={`${cat} (custom)`}
                  />
                ))}
              </FormGroup>

              <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 3 }}>
                <TextField size="small" label="Add custom category" value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)} />
                <Button variant="outlined" onClick={() => { addCustomCategory(); setEditingConfig(true); }}>
                  Add
                </Button>
              </Box>

              <TextField
                fullWidth size="small" type="date" sx={{ mb: 3, maxWidth: 320 }}
                label="Calculation Period Start Date (default)"
                InputLabelProps={{ shrink: true }}
                value={localStartDateConfig}
                onChange={(e) => { setLocalStartDateConfig(e.target.value); setEditingConfig(true); }}
                helperText="Pre-fills the Start Date on the Calculate tab, and is what a staff member's Profile 'Recalculate' button runs from. Leave blank to default to 1 January of the current year."
              />

              <Button variant="contained" startIcon={<Save />}
                onClick={handleSaveConfig} disabled={saving || !editingConfig}>
                {saving ? <CircularProgress size={22} /> : 'Save Configuration'}
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* ── Detail Dialog ── */}
      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detailRow?.name} — Detail</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Bring Forward</Typography>
                  <Typography variant="h6">{fmtHrs(detailRow.bringForward)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">New Opening Balance</Typography>
                  <Typography variant="h6" color="success.main">{fmtHrs(detailRow.newOpeningBalance)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Current Opening Balance</Typography>
                  <Typography variant="body2">{fmtHrs(detailRow.existingOpeningBalance)}</Typography>
                </Grid>
              </Grid>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <EventBusy fontSize="small" sx={{ mr: 1 }} />
                  <Typography>Annual Leave Taken ({detailRow.leaveBreakdown?.length || 0} records)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Gross Hours</TableCell>
                          <TableCell align="right">Break Deducted</TableCell>
                          <TableCell align="right">Deducted from Balance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(detailRow.leaveBreakdown || []).map((l, i) => (
                          <TableRow key={i}>
                            <TableCell>{new Date(l.date).toLocaleDateString()}</TableCell>
                            <TableCell align="right">{fmtHrs(l.grossHours ?? l.hours)}</TableCell>
                            <TableCell align="right">{fmtHrs(l.breakDeductedHours ?? 0)}</TableCell>
                            <TableCell align="right">{fmtHrs(l.hours)}</TableCell>
                          </TableRow>
                        ))}
                        {(!detailRow.leaveBreakdown || detailRow.leaveBreakdown.length === 0) && (
                          <TableRow><TableCell colSpan={4}>No Annual Leave records in this period.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Apply Confirmation Dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Calculate color="primary" /> Confirm Apply
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            This will recompute each of the {results.length} staff member(s)' Opening Balance as
            Bring Forward minus Annual Leave taken between <strong>{startDate}</strong> and{' '}
            <strong>{endDate}</strong>, and write the result to their record. Bring Forward itself is
            never changed by this.
          </Typography>
          <Alert severity="info">
            Safe to re-run: because this always recomputes fully from Bring Forward, running it again
            (e.g. after more leave is logged) will always give the correct up-to-date result.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleApply}>Confirm &amp; Apply</Button>
        </DialogActions>
      </Dialog>

      {/* ── End of Year Confirmation Dialog ── */}
      <Dialog open={endOfYearConfirmOpen} onClose={() => setEndOfYearConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" /> Confirm End of Year Rollover
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            This will copy the current Opening Balance into Bring Forward, and reset Opening Balance to 0,
            for{' '}
            <strong>
              {targetUser === 'all'
                ? `all ${eligibleUsers.length} eligible staff member(s)`
                : eligibleUsers.find((u) => u._id === targetUser)?.name || 'the selected staff member'}
            </strong>.
          </Typography>
          <Alert severity="warning">
            This overwrites each staff member's existing Bring Forward value with their current Opening
            Balance — it does not add to it. Make sure the final Preview/Apply for the outgoing year has
            already been run before doing this.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndOfYearConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleEndOfYear}>Confirm &amp; Run</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NonBankAnnualLeaveCalcScreen;
